import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CheckCircle, Shield, Zap } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const AboutHero = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.fromTo(
        '.about-hero-line',
        { scaleX: 0, opacity: 0 },
        { scaleX: 1, opacity: 1, duration: 1 }
      );

      tl.fromTo(
        '.about-hero-title',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        '-=0.5'
      );

      tl.fromTo(
        '.about-hero-subtitle',
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7 },
        '-=0.4'
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_power-pulse-18/artifacts/rubmqn2w_facade-2026-01-05-01-13-07-utc.jpg"
          alt="Modern home with security and lighting"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(229,33,51,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(229,33,51,0.2)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 pt-24 relative z-10">
        <div className="max-w-2xl">
          <div
            className="about-hero-line w-24 h-1 bg-gradient-to-r from-securelec-red to-securelec-red/50 mb-6 origin-left rounded-full"
            style={{ boxShadow: '0 0 20px rgba(229, 33, 51, 0.6)' }}
          />
          <h1 className="about-hero-title text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight drop-shadow-lg">
            About Securelec
          </h1>
          <p className="about-hero-subtitle text-lg md:text-xl text-white/80 leading-relaxed drop-shadow-md">
            Owner-operated electrical & security specialists you can trust.
          </p>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-securelec-light to-transparent" />
    </section>
  );
};

const AboutContent = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.about-text',
        { x: -60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      gsap.fromTo(
        '.about-image-block',
        { x: 60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const highlights = [
    "Registered Electrical Contractor (REC 30321)",
    "Business Security License (853-441-92S)",
    "Both owners hold personal Security & Electrical licences",
    "30+ years combined experience",
    "Direct owner involvement on every project"
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-securelec-light relative overflow-hidden">
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-64 bg-gradient-to-b from-transparent via-securelec-red to-transparent" />

      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <div>
            <span className="about-text inline-block text-securelec-red text-sm font-semibold tracking-widest uppercase mb-4">
              Our Story
            </span>
            <h2 className="about-text text-4xl md:text-5xl font-bold text-securelec-dark mb-6">
              Who We Are
            </h2>
            <div className="about-text w-16 h-1 bg-securelec-red mb-8" />
            <p className="about-text text-lg text-securelec-gray/80 mb-6 leading-relaxed">
              Securelec is a proudly Australian-owned and operated business, built on a foundation of reliability, integrity, and exceptional service. With over 30 years of combined experience in electrical and security systems, we've established ourselves as trusted specialists across Melbourne and Victoria.
            </p>
            <p className="about-text text-securelec-gray/70 mb-8 leading-relaxed">
              What sets us apart is our owner-operated model. When you work with Securelec, you're working directly with Michael and Tom – the experts who built this business from the ground up. We believe in personal accountability, which is why we're on the tools alongside our team, ensuring every project meets our exacting standards.
            </p>

            {/* Highlights */}
            <div className="about-text space-y-3 mb-8">
              {highlights.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-securelec-red flex-shrink-0" />
                  <span className="text-securelec-gray font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Image */}
          <div className="about-image-block relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_d0850d40-f350-4ecf-87ac-4e2d9723d534/artifacts/etyr6m4f_About%20us%20image.jpg"
                alt="Professional electrical and security work by Securelec Carrum"
                className="w-full h-[450px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-securelec-dark/20 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-securelec-red/10 rounded-2xl -z-10" />
            <div className="absolute -top-6 -right-6 w-20 h-20 bg-securelec-red/20 rounded-xl -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

const TeamSection = () => {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.team-title',
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      cardsRef.current.forEach((card, index) => {
        gsap.fromTo(
          card,
          { y: 60, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            delay: index * 0.2,
            scrollTrigger: {
              trigger: card,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const owners = [
    {
      name: "Michael",
      specialty: "Security & Electrical",
      icon: Shield,
      image: "https://customer-assets.emergentagent.com/job_5bafb83c-d66a-46c6-a3cc-c3c946be451b/artifacts/92ejjap8_Michael.jpg",
      bio: [
        "Michael brings extensive experience across both security and electrical industries. While his primary expertise lies in security systems—from residential alarm installations to complex commercial access control and CCTV solutions—he is also fully qualified and licensed in electrical work.",
        "Michael holds both a Business Security License (853-441-92S) and maintains his personal electrical licence, ensuring he can deliver comprehensive solutions across both disciplines. His attention to detail and commitment to staying at the forefront of technology means your property receives the best protection available.",
        "Working directly with clients to understand their unique needs, Michael ensures every solution is tailored to provide genuine peace of mind—whether that's a security upgrade, electrical work, or an integrated system that combines both."
      ]
    },
    {
      name: "Tom",
      specialty: "Electrical & Security",
      icon: Zap,
      image: "https://customer-assets.emergentagent.com/job_5bafb83c-d66a-46c6-a3cc-c3c946be451b/artifacts/6mvnxr0d_Tom.jpg",
      bio: [
        "Co-Director and founder of Securelec Services, Tom brings over 15 years of experience across residential, commercial and large-scale electrical projects. Beginning his career in 2008, he progressed through supervisory roles leading teams of electricians while developing a strong focus on quality workmanship and efficient project delivery.",
        "In 2019 Tom transitioned into commercial construction, working on complex architectural builds and major electrical installations including lighting, power, underground services, data infrastructure, and advanced lighting control systems such as DALI, C-Bus, KNX and Dynalite. He also holds his personal security licence, enabling integrated solutions across both disciplines.",
        "Tom takes pride in completing work to the highest standard, ensuring every installation is carried out strictly to specification and industry regulations. Safety, attention to detail and honest communication are values that guide his approach to every project."
      ]
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-securelec-red/5 rounded-full blur-[150px]" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="team-title inline-block text-securelec-red text-sm font-semibold tracking-widest uppercase mb-4">
            Our Team
          </span>
          <h2 className="team-title text-4xl md:text-5xl font-bold text-securelec-dark mb-6">
            Meet the <span className="text-securelec-red">Owners</span>
          </h2>
          <div className="team-title w-24 h-1 bg-gradient-to-r from-transparent via-securelec-red to-transparent mx-auto" />
        </div>

        {/* Team Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {owners.map((owner, index) => {
            const IconComponent = owner.icon;
            return (
              <div
                key={owner.name}
                ref={(el) => (cardsRef.current[index] = el)}
                className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-securelec-red/50 hover:shadow-xl transition-all duration-500"
              >
                {/* Photo */}
                <div className="relative overflow-hidden bg-securelec-light">
                  <img
                    src={owner.image}
                    alt={`${owner.name} - Licensed electrician and security installer Bayside Melbourne`}
                    className="w-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                  
                  {/* Specialty Badge */}
                  <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-securelec-red/90 backdrop-blur-sm text-white px-4 py-2 rounded-full">
                    <IconComponent className="w-4 h-4" />
                    <span className="text-sm font-semibold">{owner.specialty}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-securelec-dark mb-4 group-hover:text-securelec-red transition-colors duration-300">
                    {owner.name}
                  </h3>
                  <div className="space-y-4">
                    {owner.bio.map((paragraph, i) => (
                      <p key={i} className="text-securelec-gray/70 leading-relaxed text-base">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>

                {/* Bottom Accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-securelec-red to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export { AboutHero, AboutContent, TeamSection };
